import ApiError from "@components/GenericApiError/Error";
import Grid from "@components/Grid/Grid";
import Loader from "@components/Loader/Loader";
import { useSettings } from "@utilities/context/settings";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/user";
import {
  getCareerAspirations,
  getGenders,
  getIndustries,
  getIntendedYearsOfStudy,
  getPersonalDetails,
  getPreferredStudyFormat,
  getPreferredStudyLocation,
  getTitles,
} from "../../services/forms";
import Cta from "../Cta/Cta";

import FieldJSX from "@components/Form/FieldJSX/FieldJSX";
import { IsLoggedIn } from "@components/GuestView/IsLoggedIn";
import { PROFILE_PAGE_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import { findMatchingSelectValue } from "@utilities/mapSelectValue";
import type { UserProfileResponse } from "../../types";
import styles from "./C63_1_MyProfilePersonalDetails.module.scss";
import { EditDetailsBtn } from "./EditDetailsBtn";

export interface ProfileDetailsProps {
  aboutYouTitle: string;
  countryCodeLabelText: string;
  educationalInstitutesLabelText: string;
  emailLabelText: string;
  firstNameLabelText: string;
  genderLabelText: string;
  gmatScoreLabelText: string;
  gmatFocusScoreLabelText: string;
  gmatTotalScoreLabelText: string;
  gmatSuperscoreLabelText: string;
  greAwaPercentileLabelText: string;
  greAwaScoreLabelText: string;
  greInformationTitle: string;
  greQuantitativePercentileLabelText: string;
  greQuantitativeScoreLabelText: string;
  greVerbalPercentileLabelText: string;
  greVerbalScoreLabelText: string;
  highestDegreeLabelText: string;
  homeAddress1LabelText: string;
  homeAddress2LabelText: string;
  homeCityLabelText: string;
  homeCountyStateLabelText: string;
  homePostcodeLabelText: string;
  industryLabelText: string;
  linkedInProfileLabelText: string;
  loginMessage: string;
  yourProfessionalProfileTitle: string;
  nationalityLabelText: string;
  personalInformationTitleLabelText: string;
  secondNationalityLabelText: string;
  skypeAccountLabelText: string;
  telephoneNumberLabelText: string;
  titleLabelText: string;
  workJobTitleLabelText: string;
  yearsManagerialExperienceLabelText: string;
  yearsRelevantExperienceLabelText: string;
  yourEducationProfileTitle: string;
  careerAspirationsLabelText: string;
  workCompanyNameLabelText: string;
  gmatInformationTitle: string;
  familyNameLabelText: string;
  yourAspirationsTitle: string;
  homeCountryLabelText: string;
  facebookAccountLabelText: string;
  twitterAccountLabelText: string;
  preferredStudyLocationLabelText: string;
  preferredStudyFormatLabelText: string;
  intendedYearOfStudyLabelText: string;
  changeEmailText: string;
  changePasswordText: string;
  deactivateAccountText: string;
  yourRightText: string;
}

export const MyProfilePersonalDetails = (props: {
  content: ProfileDetailsProps;
}) => {
  const { content } = props;
  const {
    aboutYouTitle,
    educationalInstitutesLabelText,
    firstNameLabelText,
    genderLabelText,
    gmatTotalScoreLabelText,
    gmatFocusScoreLabelText,
    gmatSuperscoreLabelText = "GMAT Superscore",
    greAwaPercentileLabelText,
    greAwaScoreLabelText,
    greInformationTitle,
    yourProfessionalProfileTitle,
    greQuantitativePercentileLabelText,
    greQuantitativeScoreLabelText,
    greVerbalPercentileLabelText,
    greVerbalScoreLabelText,
    highestDegreeLabelText,
    homeAddress1LabelText,
    homeAddress2LabelText,
    homeCityLabelText,
    homeCountyStateLabelText,
    homePostcodeLabelText,
    industryLabelText,
    linkedInProfileLabelText,
    loginMessage,
    nationalityLabelText,
    yourEducationProfileTitle,
    careerAspirationsLabelText,
    workCompanyNameLabelText,
    gmatInformationTitle,
    familyNameLabelText,
    yourAspirationsTitle,
    homeCountryLabelText,
    facebookAccountLabelText,
    twitterAccountLabelText,
    personalInformationTitleLabelText,
    secondNationalityLabelText,
    skypeAccountLabelText,
    telephoneNumberLabelText,
    titleLabelText,
    workJobTitleLabelText,
    yearsManagerialExperienceLabelText,
    yearsRelevantExperienceLabelText,
    preferredStudyLocationLabelText,
    preferredStudyFormatLabelText,
    intendedYearOfStudyLabelText,
    // this var will be needed  later

    changeEmailText,
    changePasswordText,
    deactivateAccountText,
    yourRightText,
  } = content;

  // TODO: fix personalDetails type later

  const [personalDetails, setPersonalDetails] =
    useState<UserProfileResponse | null>(null);
  const [error, setError] = useState<boolean>(false);
  const { siteSettings } = useSettings();
  const { user } = useContext(UserContext);

  useEffect(() => {
    const personalDetailsAPI = async () => {
      if (user) {
        try {
          const details = await getPersonalDetails();
          const titles = await getTitles();
          const genders = await getGenders();
          const industries = await getIndustries();
          const careerAspirations = await getCareerAspirations();
          const preferredStudyFormats = await getPreferredStudyFormat();
          const preferredStudyLocations = await getPreferredStudyLocation();
          const intendedYearsOfStudy = await getIntendedYearsOfStudy();
          const title = findMatchingSelectValue(titles, details?.result?.title);
          const gender = findMatchingSelectValue(
            genders,
            details?.result?.gender
          );
          const industry = findMatchingSelectValue(
            industries,
            details?.result?.industry
          );
          const careerAspiration = findMatchingSelectValue(
            careerAspirations,
            details?.result?.careerAspiration
          );
          const intendedYearOfStudy = findMatchingSelectValue(
            intendedYearsOfStudy,
            details?.result?.intendedYearOfStudy
          );
          const preferredStudyFormat = findMatchingSelectValue(
            preferredStudyFormats,
            details?.result?.preferredStudyFormat
          );
          const preferredStudyLocation = findMatchingSelectValue(
            preferredStudyLocations,
            details?.result?.preferredStudyLocation
          );

          setPersonalDetails({
            ...details?.result,
            title,
            gender,
            industry,
            careerAspiration,
            intendedYearOfStudy,
            preferredStudyFormat,
            preferredStudyLocation,
          });
        } catch (e) {
          console.log(e);
          setError(true);
        }
      }
    };

    personalDetailsAPI();
  }, [user]);

  if (error) {
    return <ApiError />;
  }

  const aboutYouFields = [
    { labelText: titleLabelText, dataText: personalDetails?.title },

    { labelText: firstNameLabelText, dataText: personalDetails?.firstName },

    { labelText: familyNameLabelText, dataText: personalDetails?.lastName },

    { labelText: genderLabelText, dataText: personalDetails?.gender },

    { labelText: nationalityLabelText, dataText: personalDetails?.nationality },

    {
      labelText: secondNationalityLabelText,
      dataText: personalDetails?.secondNationality,
    },
  ];

  const personalInformationFields = [
    // To do: will be implemented later
    /*    {
      labelText: emailLabelText,
      dataText: personalDetails?.emailAddress,
    },*/
    {
      labelText: telephoneNumberLabelText,
      dataText: personalDetails?.telephoneNumber,
    },
    {
      labelText: homeAddress1LabelText,
      dataText: personalDetails?.address?.addressLine1,
    },
    {
      labelText: homeAddress2LabelText,
      dataText: personalDetails?.address?.addressLine2,
    },
    {
      labelText: homeCityLabelText,
      dataText: personalDetails?.address?.city,
    },
    {
      labelText: homeCountyStateLabelText,
      dataText: personalDetails?.address?.stateCounty,
    },
    {
      labelText: homePostcodeLabelText,
      dataText: personalDetails?.address?.postCode,
    },
    {
      labelText: homeCountryLabelText,
      dataText: personalDetails?.address?.homeCountry,
    },

    {
      labelText: facebookAccountLabelText,
      dataText: personalDetails?.facebookProfile,
    },

    {
      labelText: twitterAccountLabelText,
      dataText: personalDetails?.twitterHandle,
    },

    {
      labelText: skypeAccountLabelText,
      dataText: personalDetails?.skypeId,
    },

    {
      labelText: linkedInProfileLabelText,
      dataText: personalDetails?.linkedInUrl,
    },
  ];

  const professionalDetails = personalDetails?.employment;
  const professionalProfileFields = [
    {
      dataText: professionalDetails?.jobTitle,
      labelText: workJobTitleLabelText,
    },

    {
      dataText: professionalDetails?.company,
      labelText: workCompanyNameLabelText,
    },

    {
      dataText: personalDetails?.yearsOfExperience,
      labelText: yearsRelevantExperienceLabelText,
    },

    {
      dataText: personalDetails?.yearsOfManagerialExperience,
      labelText: yearsManagerialExperienceLabelText,
    },

    {
      dataText: professionalDetails?.address?.addressLine1,
      labelText: homeAddress1LabelText,
    },

    {
      dataText: professionalDetails?.address?.addressLine2,
      labelText: homeAddress2LabelText,
    },

    {
      dataText: professionalDetails?.address?.city,
      labelText: homeCityLabelText,
    },

    {
      dataText: professionalDetails?.address?.stateCounty,
      labelText: homeCountyStateLabelText,
    },

    {
      dataText: professionalDetails?.address?.postCode,

      labelText: homePostcodeLabelText,
    },

    {
      dataText: professionalDetails?.address?.homeCountry,
      labelText: homeCountryLabelText,
    },
    {
      dataText: personalDetails?.industry,
      labelText: industryLabelText,
    },
  ];

  const educationProfileFields = [
    {
      dataText: personalDetails?.education?.academicInstitution,
      labelText: educationalInstitutesLabelText,
    },
    {
      dataText: personalDetails?.education?.highestDegree,
      labelText: highestDegreeLabelText,
    },
  ];

  const greInformationFields = [
    {
      dataText: personalDetails?.scores?.verbalScore,
      labelText: greVerbalScoreLabelText,
    },
    {
      dataText: personalDetails?.scores?.verbalPercentile,
      labelText: greVerbalPercentileLabelText,
    },
    {
      dataText: personalDetails?.scores?.quantativeScore,
      labelText: greQuantitativeScoreLabelText,
    },
    {
      dataText: personalDetails?.scores?.quantativePercentile,
      labelText: greQuantitativePercentileLabelText,
    },
    {
      dataText: personalDetails?.scores?.awaScore,
      labelText: greAwaScoreLabelText,
    },
    {
      dataText: personalDetails?.scores?.awaPercentile,
      labelText: greAwaPercentileLabelText,
    },
  ];

  const aspirationsFields = [
    {
      dataText: personalDetails?.careerAspiration,
      labelText: careerAspirationsLabelText,
    },

    {
      dataText: personalDetails?.intendedYearOfStudy,
      labelText: intendedYearOfStudyLabelText,
    },

    {
      dataText: personalDetails?.preferredStudyFormat,
      labelText: preferredStudyFormatLabelText,
    },

    {
      dataText: personalDetails?.preferredStudyLocation,
      labelText: preferredStudyLocationLabelText,
    },
  ];

  if (!personalDetails) {
    return (
      <div className={styles.spinner}>
        <Loader
          color="#001e62"
          size={70}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Grid row>
        <Grid column sm={12} md={6}>
          <div className="component form"></div>
          <section
            className={clsx(
              styles.component,
              styles.form,
              styles["my-profile"],
              styles.cf
            )}
          >
            <div className={styles["fields-floated"]}>
              {!user ? (
                <p>{loginMessage}</p>
              ) : (
                <>
                  <h2
                    {...getE2EAnchorAttribute(
                      PROFILE_PAGE_ANCHOR,
                      "about-title"
                    )}
                    className={styles["section-heading"]}
                  >
                    {aboutYouTitle}
                  </h2>
                  {aboutYouFields.map((field, index) => (
                    <FieldJSX
                      key={`${field.labelText}-${index}`}
                      labelText={field.labelText}
                      dataText={field.dataText}
                      customClass={styles["field-row"]}
                      labelProps={getE2EAnchorAttribute(
                        PROFILE_PAGE_ANCHOR,
                        `${field.labelText}-label`
                      )}
                      valueProps={getE2EAnchorAttribute(
                        PROFILE_PAGE_ANCHOR,
                        `${field.labelText}-value`
                      )}
                    />
                  ))}
                  <EditDetailsBtn sectionTitle={aboutYouTitle} />
                  <h2 className={styles["section-heading"]}>
                    <span>{personalInformationTitleLabelText}</span>
                  </h2>
                  {personalInformationFields.map((field, index) => (
                    <FieldJSX
                      key={`${field.labelText}-${index}`}
                      labelText={field.labelText}
                      dataText={field.dataText}
                      customClass={styles["field-row"]}
                    />
                  ))}
                  <EditDetailsBtn
                    sectionTitle={personalInformationTitleLabelText}
                  />
                  <h2 className={styles["section-heading"]}>
                    <span>{yourProfessionalProfileTitle}</span>
                  </h2>
                  {professionalProfileFields.map((field, index) => (
                    <FieldJSX
                      key={`${field.labelText}-${index}`}
                      labelText={field.labelText}
                      dataText={field.dataText}
                      customClass={styles["field-row"]}
                    />
                  ))}
                  <EditDetailsBtn sectionTitle={yourProfessionalProfileTitle} />
                  <h2 className={styles["section-heading"]}>
                    <span>{yourEducationProfileTitle}</span>
                  </h2>
                  {educationProfileFields.map((field, index) => (
                    <FieldJSX
                      key={`${field.labelText}-${index}`}
                      labelText={field.labelText}
                      dataText={field.dataText}
                      customClass={styles["field-row"]}
                    />
                  ))}
                  <h3 className={styles["sub-heading"]}>
                    {gmatInformationTitle}
                  </h3>
                  <FieldJSX
                    dataText={personalDetails?.scores?.gmatScore}
                    labelText={gmatTotalScoreLabelText}
                    customClass={styles["field-row"]}
                  />
                  <FieldJSX
                    dataText={personalDetails?.scores?.gmatFocusTotalScore}
                    labelText={gmatFocusScoreLabelText}
                    customClass={styles["field-row"]}
                  />
                  <FieldJSX
                    dataText={personalDetails?.scores?.gmatSuperscore}
                    labelText={gmatSuperscoreLabelText}
                    customClass={styles["field-row"]}
                  />

                  <h3 className={styles["sub-heading"]}>
                    {greInformationTitle}
                  </h3>

                  {greInformationFields.map((field, index) => (
                    <FieldJSX
                      key={`${field.labelText}-${index}`}
                      labelText={field.labelText}
                      dataText={field.dataText}
                      customClass={styles["field-row"]}
                    />
                  ))}
                  <EditDetailsBtn sectionTitle={yourEducationProfileTitle} />
                  <h2 className={styles["section-heading"]}>
                    <span>{yourAspirationsTitle}</span>
                  </h2>

                  {aspirationsFields.map((field, index) => (
                    <FieldJSX
                      key={`${field.labelText}-${index}`}
                      labelText={field.labelText}
                      dataText={field.dataText}
                      customClass={styles["field-row"]}
                    />
                  ))}
                  <EditDetailsBtn sectionTitle={yourAspirationsTitle} />
                </>
              )}
            </div>
          </section>
        </Grid>
        <Grid column sm={12} md={6}>
          <h2 className={styles["section-heading"]}>Login Details</h2>
          <ul>
            <li>Email: {personalDetails?.emailAddress}</li>

            <li>
              <Cta
                icon="icon-arrow"
                url="/confirm-change-email"
                linkText={changeEmailText}
                type="tertiary"
              />
            </li>

            <li>
              <Cta
                icon="icon-arrow"
                url={
                  siteSettings ? siteSettings?.changePasswordPage?.slug : "#"
                }
                linkText={changePasswordText}
                type="tertiary"
              />
            </li>

            <li>
              <Cta
                icon="icon-arrow"
                url={
                  siteSettings ? siteSettings?.deactivateAccountPage?.slug : "#"
                }
                linkText={deactivateAccountText}
                type="tertiary"
              />
            </li>

            <li>
              <Cta
                icon="icon-arrow"
                url={
                  siteSettings ? siteSettings?.yourRightsToDataPage?.slug : "#"
                }
                linkText={yourRightText}
                type="tertiary"
              />
            </li>
          </ul>
        </Grid>
      </Grid>
    </div>
  );
};

export default IsLoggedIn(MyProfilePersonalDetails);
